/**
 * Rebuild the finest level of an LOD / streamed-SOG splat for editing.
 *
 * The octree resource already knows, per leaf node, which file and row range
 * holds each LOD level. Level 0 of every node is exactly what the viewer draws
 * at full detail, so the edit model is the union of those ranges (falling back
 * to a node's finest available level when level 0 is missing, which LCC2
 * gap-filled manifests can produce). Each referenced file is loaded once with
 * the engine's own SOG parser in decompress mode, the ranges are copied out,
 * and the temporary resources are released.
 *
 * The environment (e.g. LCC2 sky) is appended after the node data and its row
 * range is returned, so a save can hand it back to the LOD writer separately.
 */
import * as pc from 'playcanvas';
import type { SplatColumnSource } from '../core/SplatEditModel';
import { CORE_PROPS, SH_COEFFS_PER_CHANNEL, shBandsFromRestCount } from '../core/SplatEditModel';
import { ColumnSource, copyRows, decodeGsplatResource } from './decodeSource';

interface OctreeLod { file: string; fileIndex: number; offset: number; count: number }
interface OctreeLike {
    lodLevels: number;
    files: { url: string; lodLevel: number }[];
    nodes: { lods: OctreeLod[] }[];
    environmentUrl?: string | null;
}

export interface LodSourceInfo {
    source: SplatColumnSource;
    /** Rows [start, end) holding the environment, or null. */
    environmentRange: [number, number] | null;
    lodLevels: number;
    filesLoaded: number;
    /** LOD level that was loaded (0 = full detail). */
    lodLevel: number;
    /** Splat count of each level (finest first). */
    levelCounts: number[];
}

export interface LodLoadOptions {
    onProgress?: (loaded: number, total: number) => void;
    /** Abort when the session is cancelled. */
    signal?: AbortSignal;
    /** Parallel file loads. */
    concurrency?: number;
    /**
     * Load the finest level whose total splat count fits this budget (the
     * coarsest level when none does). Default: always level 0.
     */
    maxSplats?: number;
}

export function isOctreeResource(resource: unknown): resource is { octree: OctreeLike } {
    const o = (resource as { octree?: OctreeLike } | null)?.octree;
    return !!o && Array.isArray(o.nodes) && Array.isArray(o.files);
}

/** Total splats each LOD level renders (a node missing a level uses its next finer one). */
export function levelCounts(octree: OctreeLike): number[] {
    const counts: number[] = [];
    for (let level = 0; level < octree.lodLevels; level++) {
        let total = 0;
        const seen = new Set<string>();
        for (const node of octree.nodes) {
            const lod = lodAtOrFiner(node.lods, level);
            if (!lod) continue;
            const key = `${lod.fileIndex}:${lod.offset}:${lod.count}`;
            if (seen.has(key)) continue;
            seen.add(key);
            total += lod.count;
        }
        counts.push(total);
    }
    return counts;
}

function lodAtOrFiner(lods: OctreeLod[], level: number): OctreeLod | undefined {
    const ok = (l: OctreeLod | undefined) => !!l && l.fileIndex >= 0 && l.count > 0;
    if (ok(lods[level])) return lods[level];
    for (let k = level - 1; k >= 0; k--) if (ok(lods[k])) return lods[k];
    for (let k = level + 1; k < lods.length; k++) if (ok(lods[k])) return lods[k];
    return undefined;
}

/** Row ranges per file for one LOD level (default: the finest level). */
export function finestRanges(octree: OctreeLike, level = 0): Map<number, { offset: number; count: number }[]> {
    const perFile = new Map<number, { offset: number; count: number }[]>();
    const seen = new Set<string>();
    for (const node of octree.nodes) {
        const lod = level === 0 ? node.lods.find((l) => l && l.fileIndex >= 0 && l.count > 0) : lodAtOrFiner(node.lods, level);
        if (!lod) continue;
        const key = `${lod.fileIndex}:${lod.offset}:${lod.count}`;
        if (seen.has(key)) continue; // aliased slots can repeat a range
        seen.add(key);
        let list = perFile.get(lod.fileIndex);
        if (!list) { list = []; perFile.set(lod.fileIndex, list); }
        list.push({ offset: lod.offset, count: lod.count });
    }
    return perFile;
}

async function loadDecompressed(app: pc.AppBase, url: string): Promise<{ source: SplatColumnSource; release: () => void }> {
    const asset = new pc.Asset(`splat-edit-lod-${url}`, 'gsplat', { url }, { decompress: true } as never);
    app.assets.add(asset);
    try {
        await new Promise<void>((resolve, reject) => {
            asset.once('load', () => resolve());
            asset.once('error', (err: unknown) => reject(err instanceof Error ? err : new Error(String(err))));
            app.assets.load(asset);
        });
        const source = await decodeGsplatResource(asset.resource);
        if (!source) throw new Error(`Could not decode LOD chunk ${url}`);
        return {
            source,
            release: () => {
                app.assets.remove(asset);
                asset.unload();
            },
        };
    } catch (err) {
        app.assets.remove(asset);
        asset.unload();
        throw err;
    }
}

export async function loadLodSource(app: pc.AppBase, octreeResource: unknown, options: LodLoadOptions = {}): Promise<LodSourceInfo> {
    if (!isOctreeResource(octreeResource)) throw new Error('loadLodSource: not an octree resource');
    const octree = octreeResource.octree;
    const counts = levelCounts(octree);
    let level = 0;
    if (options.maxSplats && counts.length) {
        level = counts.findIndex((c) => c <= options.maxSplats!);
        if (level < 0) level = counts.length - 1;
    }
    const ranges = finestRanges(octree, level);
    const fileIndices = [...ranges.keys()].sort((a, b) => a - b);
    const envUrl = octree.environmentUrl ?? null;
    const total = fileIndices.length + (envUrl ? 1 : 0);

    let rows = 0;
    for (const list of ranges.values()) for (const r of list) rows += r.count;

    // Columns are allocated once the first file tells us the SH band count.
    let columns: Map<string, Float32Array> | null = null;
    let shBands = 3;
    const ensureColumns = (src: SplatColumnSource, totalRows: number) => {
        if (columns) return;
        let rest = 0;
        while (rest < 45 && src.getProp(`f_rest_${rest}`)) rest++;
        shBands = shBandsFromRestCount(rest);
        columns = new Map();
        for (const name of CORE_PROPS) columns.set(name, new Float32Array(totalRows));
        for (let k = 0; k < SH_COEFFS_PER_CHANNEL[shBands] * 3; k++) columns.set(`f_rest_${k}`, new Float32Array(totalRows));
    };

    // Environment rows are appended after node rows; size is unknown until loaded.
    let envSource: { source: SplatColumnSource; release: () => void } | null = null;
    let loaded = 0;
    if (envUrl) {
        envSource = await loadDecompressed(app, envUrl);
        loaded++;
        options.onProgress?.(loaded, total);
    }
    const envRows = envSource?.source.numSplats ?? 0;

    const concurrency = Math.max(1, options.concurrency ?? 4);
    let next = 0;
    let at = 0;
    const offsets = new Map<number, number>();
    for (const fi of fileIndices) {
        offsets.set(fi, at);
        for (const r of ranges.get(fi)!) at += r.count;
    }

    const worker = async () => {
        while (next < fileIndices.length) {
            if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
            const fi = fileIndices[next++];
            const url = octree.files[fi].url;
            const file = await loadDecompressed(app, url);
            try {
                ensureColumns(file.source, rows + envRows);
                let dst = offsets.get(fi)!;
                for (const r of ranges.get(fi)!) {
                    copyRows(file.source, columns!, dst, r.offset, r.count);
                    dst += r.count;
                }
            } finally {
                file.release();
            }
            loaded++;
            options.onProgress?.(loaded, total);
        }
    };
    try {
        await Promise.all(Array.from({ length: Math.min(concurrency, fileIndices.length) }, worker));
        if (envSource) {
            ensureColumns(envSource.source, rows + envRows);
            copyRows(envSource.source, columns!, rows, 0, envRows);
        }
    } finally {
        envSource?.release();
    }

    if (!columns) throw new Error('loadLodSource: manifest references no splat data');
    const cols = columns as Map<string, Float32Array>;
    // default rotations for any chunk missing them (should not happen for SOG)
    const r0 = cols.get('rot_0')!;
    let anyRot = false;
    for (let i = 0; i < r0.length && !anyRot; i++) if (r0[i] !== 0) anyRot = true;
    if (!anyRot) r0.fill(1);

    return {
        source: new ColumnSource(rows + envRows, cols),
        environmentRange: envRows > 0 ? [rows, rows + envRows] : null,
        lodLevels: octree.lodLevels,
        filesLoaded: loaded,
        lodLevel: level,
        levelCounts: counts,
    };
}
